import { Clock4, CalendarDays, ChartLine } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Two orthogonal switches on top of any time-series chart:
 *
 *   - Granularity (Hour / Day / Week)   — what the server buckets by.
 *   - Seasonality (Off / Hour-of-day / Day-of-week) — how the client
 *                                                    collapses the series
 *                                                    *after* it loads.
 *
 * The component is intentionally dumb: callers own state and disabled-rules
 * (e.g. greying out 'hour' when the range is too wide). We render compact
 * pill buttons that fit a Card header next to the existing Badge.
 */

export type ChartGranularity = 'hour' | 'day' | 'week';
export type SeasonalityMode = 'none' | 'hour-of-day' | 'day-of-week';

interface ChartModeToggleProps {
  granularity: ChartGranularity;
  onGranularityChange: (g: ChartGranularity) => void;
  /** Disable specific granularities (e.g. `hour` when the date range > 7d). */
  disabledGranularities?: ChartGranularity[];
  seasonality: SeasonalityMode;
  onSeasonalityChange: (s: SeasonalityMode) => void;
  className?: string;
}

const GRAN_OPTIONS: { value: ChartGranularity; label: string }[] = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
];

const SEASON_OPTIONS: { value: SeasonalityMode; label: string; short: string }[] = [
  { value: 'none', label: 'Time series', short: 'Series' },
  { value: 'hour-of-day', label: 'Hour of day (0–23)', short: 'Hour-of-day' },
  { value: 'day-of-week', label: 'Day of week (Sun–Sat)', short: 'Day-of-week' },
];

export function ChartModeToggle({
  granularity,
  onGranularityChange,
  disabledGranularities,
  seasonality,
  onSeasonalityChange,
  className,
}: ChartModeToggleProps) {
  // When seasonality is on, granularity becomes implicit (we re-bucket on the
  // client) so we visually de-emphasise but still let the user change it —
  // it controls how the *raw* data is fetched before we collapse it.
  const seasonalityActive = seasonality !== 'none';

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Segment
        ariaLabel="Chart view"
        icon={<ChartLine className="h-3 w-3" aria-hidden />}
      >
        {SEASON_OPTIONS.map((o) => (
          <SegmentButton
            key={o.value}
            active={seasonality === o.value}
            onClick={() => onSeasonalityChange(o.value)}
            title={o.label}
          >
            {o.short}
          </SegmentButton>
        ))}
      </Segment>
      <Segment
        ariaLabel="Granularity"
        icon={
          seasonalityActive ? (
            <CalendarDays className="h-3 w-3 opacity-60" aria-hidden />
          ) : (
            <Clock4 className="h-3 w-3" aria-hidden />
          )
        }
        muted={seasonalityActive}
      >
        {GRAN_OPTIONS.map((o) => {
          const disabled = disabledGranularities?.includes(o.value) ?? false;
          return (
            <SegmentButton
              key={o.value}
              active={granularity === o.value}
              disabled={disabled}
              onClick={() => onGranularityChange(o.value)}
              title={
                disabled
                  ? `${o.label} disabled for the current range`
                  : `Bucket by ${o.label.toLowerCase()}`
              }
            >
              {o.label}
            </SegmentButton>
          );
        })}
      </Segment>
    </div>
  );
}

function Segment({
  children,
  ariaLabel,
  icon,
  muted,
}: {
  children: React.ReactNode;
  ariaLabel: string;
  icon?: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-border bg-surface p-0.5 text-[11px]',
        muted && 'opacity-70',
      )}
    >
      {icon ? <span className="ml-1 mr-0.5 text-muted-foreground">{icon}</span> : null}
      {children}
    </div>
  );
}

function SegmentButton({
  active,
  disabled,
  onClick,
  children,
  title,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={cn(
        'press inline-flex h-6 items-center rounded-sm px-2 font-medium transition-colors focus-ring',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground',
      )}
    >
      {children}
    </button>
  );
}
